// Phase 2.2.6 — Infrastructure Security control metadata.
// Editorial / informational only. Does NOT affect scoring.
//
// Covers all 44 controls in telecom_infrastructure_control_catalog:
//   12 generic (B1–B4)  +  15 telecom v2 batch 1  +  17 telecom v2 batch 2
//
// Parity with the catalog is enforced by:
//   src/configs/controlGuidance/__tests__/infrastructureControlMetadata.spec.ts

export type ServiceModel =
  | "MNO"
  | "MVNO"
  | "Integrated Operator"
  | "FiberCo"
  | "TowerCo"
  | "Wholesale Carrier"
  | "ISP"
  | "Private 5G"
  | "Cloud-native Telco"
  | "IoT"
  | "Fixed"
  | "All";

export type ModuleId = "B1" | "B2" | "B3" | "B4" | "C1" | "C2" | "C3" | "C4" | "C5" | "C6" | "C7" | "C8";

export interface InfrastructureControlMetadata {
  controlKey: string;
  label: string;
  module: ModuleId;
  description: string;
  whyItMatters: string;
  nistMappings: string[];
  telecomStandards: string[];
  evidenceExamples: string[];
  assessmentRubric: {
    implemented: string;
    partial: string;
    notImplemented: string;
  };
  applicableServiceModels: ServiceModel[];
}

const ALL: ServiceModel[] = ["All"];

const M = (m: Partial<InfrastructureControlMetadata> & { controlKey: string }): InfrastructureControlMetadata => ({
  label: m.controlKey,
  module: "B1",
  description: "",
  whyItMatters: "",
  nistMappings: [],
  telecomStandards: [],
  evidenceExamples: [],
  assessmentRubric: { implemented: "", partial: "", notImplemented: "" },
  applicableServiceModels: ALL,
  ...m,
});

export const INFRASTRUCTURE_CONTROL_METADATA: Record<string, InfrastructureControlMetadata> = {
  // ─────────────────────────────────────────────────────────────────────────
  // B1–B4 · Generic infrastructure controls (v1_flat scoring)
  // ─────────────────────────────────────────────────────────────────────────
  facility_access_controls: M({
    controlKey: "facility_access_controls",
    label: "Facility access controls",
    module: "B1",
    description: "Physical barriers and access management at facility entry points protecting telecom infrastructure.",
    whyItMatters: "Unauthorized physical access to telecom facilities can compromise network integrity, customer data and service availability.",
    nistMappings: ["PR.AA-06", "PR.IR-02"],
    telecomStandards: ["ISO/IEC 27001 A.7.1", "TIA-942 Physical Security"],
    evidenceExamples: ["Documented access control policy", "Recent access review report", "Photos / floor plans of perimeter controls"],
    assessmentRubric: {
      implemented: "Multi-factor physical access at perimeter and sensitive zones; periodic access reviews; tailgating prevention.",
      partial: "Single-factor entry, inconsistent review cycle, or missing controls in some areas.",
      notImplemented: "No formal access control or shared/unmanaged credentials.",
    },
  }),
  visitor_management: M({
    controlKey: "visitor_management",
    label: "Visitor management",
    module: "B1",
    description: "Process for identifying, authorizing, escorting and logging non-employee facility visitors.",
    whyItMatters: "Unmanaged visitors create insider-threat exposure and audit gaps for regulated telecom environments.",
    nistMappings: ["PR.AA-06"],
    telecomStandards: ["ISO/IEC 27001 A.7.2"],
    evidenceExamples: ["Visitor management procedure", "Sample visitor log (redacted)", "Visitor badge sample"],
    assessmentRubric: {
      implemented: "Pre-registration, ID verification, escort in restricted areas, ≥12-month log retention.",
      partial: "Logs maintained but escort or pre-registration inconsistently enforced.",
      notImplemented: "No visitor log or no escort policy.",
    },
  }),
  badge_biometric_access: M({
    controlKey: "badge_biometric_access",
    label: "Badge / biometric access",
    module: "B1",
    description: "Electronic credentialing for personnel access to facilities and sensitive zones.",
    whyItMatters: "Strong authentication of personnel at the physical layer prevents credential sharing and supports forensic traceability.",
    nistMappings: ["PR.AA-01", "PR.AA-06"],
    telecomStandards: ["ISO/IEC 27001 A.7.2"],
    evidenceExamples: ["Badge issuance / revocation procedure", "Sample zone-entry audit log", "HR-to-access deprovisioning workflow"],
    assessmentRubric: {
      implemented: "Unique credentials per person; biometric or MFA for high-sensitivity zones; automated revocation on offboarding.",
      partial: "Unique badges issued but no biometric/MFA on sensitive zones, or manual revocation.",
      notImplemented: "Shared badges or no electronic access system.",
    },
  }),
  cctv_coverage: M({
    controlKey: "cctv_coverage",
    label: "CCTV coverage",
    module: "B1",
    description: "Video surveillance of facility perimeters, entry points and critical infrastructure areas.",
    whyItMatters: "Surveillance deters unauthorized activity and provides forensic evidence following physical incidents.",
    nistMappings: ["DE.CM-02", "PR.IR-02"],
    telecomStandards: ["TIA-942", "ISO/IEC 27001 A.7.4"],
    evidenceExamples: ["Camera coverage map", "Retention policy", "Recent footage review log"],
    assessmentRubric: {
      implemented: "All entry/exit and critical rooms covered; 30–90 day retention; tamper alerts.",
      partial: "Partial coverage or shortened retention.",
      notImplemented: "No CCTV or recordings unavailable.",
    },
  }),
  rack_cage_security: M({
    controlKey: "rack_cage_security",
    label: "Rack / cage security",
    module: "B1",
    description: "Physical isolation of telecom equipment within locked racks or caged enclosures.",
    whyItMatters: "Rack-level controls limit blast radius of an intrusion and segregate customer or function-specific equipment.",
    nistMappings: ["PR.IR-02"],
    telecomStandards: ["TIA-942", "ISO/IEC 27001 A.7.5"],
    evidenceExamples: ["Rack/cage access policy", "Photos of locked enclosures", "Smart-lock audit log"],
    assessmentRubric: {
      implemented: "Locked racks/cages with rack-level audit logs; tamper-evident seals on critical equipment.",
      partial: "Locked racks but no per-rack logging.",
      notImplemented: "Open racks or shared cages without isolation.",
    },
  }),
  fire_suppression: M({
    controlKey: "fire_suppression",
    label: "Fire suppression",
    module: "B2",
    description: "Detection and suppression systems protecting equipment areas from fire damage.",
    whyItMatters: "Fire events can cause prolonged service outages and total loss of network equipment.",
    nistMappings: ["PR.IR-04"],
    telecomStandards: ["NFPA 75", "TIA-942"],
    evidenceExamples: ["Most recent inspection certificate", "Suppression schematic", "Alarm test records"],
    assessmentRubric: {
      implemented: "Clean-agent suppression in equipment rooms; annual inspection; 24/7 monitored alarms.",
      partial: "Sprinkler-only or overdue inspections.",
      notImplemented: "No suppression system or no monitoring.",
    },
  }),
  hvac_environmental: M({
    controlKey: "hvac_environmental",
    label: "HVAC / environmental monitoring",
    module: "B2",
    description: "Climate control and environmental monitoring for equipment rooms.",
    whyItMatters: "Out-of-spec temperature or humidity causes equipment failure, reduces lifespan and triggers thermal shutdowns.",
    nistMappings: ["PR.IR-04", "DE.CM-02"],
    telecomStandards: ["ASHRAE TC 9.9", "TIA-942"],
    evidenceExamples: ["HVAC maintenance log", "Environmental monitoring dashboard", "Recent alert/incident records"],
    assessmentRubric: {
      implemented: "N+1 HVAC; continuous monitoring with alerting; documented ASHRAE operating envelope.",
      partial: "Monitoring exists but no redundancy or no alerting.",
      notImplemented: "No HVAC redundancy or monitoring.",
    },
  }),
  ups_generator: M({
    controlKey: "ups_generator",
    label: "UPS / generator resilience",
    module: "B3",
    description: "Backup power systems sustaining operations during utility outages.",
    whyItMatters: "Telecom service continuity depends on uninterrupted power; outages translate directly to customer impact.",
    nistMappings: ["PR.IR-04", "RC.RP-01"],
    telecomStandards: ["TIA-942", "Uptime Institute Tier"],
    evidenceExamples: ["UPS/generator test logs", "Fuel contract / refueling SLA", "Load capacity calculations"],
    assessmentRubric: {
      implemented: "UPS sized for graceful generator handoff; ≥48h on-site fuel; monthly load tests; annual full transfer test.",
      partial: "Backup exists but tests irregular or fuel insufficient.",
      notImplemented: "No UPS or generator, or untested.",
    },
  }),
  network_room_protection: M({
    controlKey: "network_room_protection",
    label: "Network room protection",
    module: "B4",
    description: "Enhanced controls protecting MDF/IDF rooms, core routers and switching equipment.",
    whyItMatters: "Network rooms are single points of failure; physical compromise can disrupt or intercept all traffic.",
    nistMappings: ["PR.IR-02", "PR.AA-06"],
    telecomStandards: ["ISO/IEC 27001 A.7.4"],
    evidenceExamples: ["Network room access procedure", "Sensor configuration export", "Sample entry log"],
    assessmentRubric: {
      implemented: "Restricted access with separate credential set; environmental + intrusion sensors; reason-for-access logged.",
      partial: "Restricted-access room but minimal sensors or logging.",
      notImplemented: "Shared access or no protective separation.",
    },
  }),
  cable_pathway_security: M({
    controlKey: "cable_pathway_security",
    label: "Cable / pathway security",
    module: "B4",
    description: "Protection of cable runs, conduits, risers and entry facilities from tampering or damage.",
    whyItMatters: "Exposed pathways enable wiretapping, accidental cuts or targeted sabotage of communications links.",
    nistMappings: ["PR.IR-02"],
    telecomStandards: ["TIA-758", "ANSI/TIA-942"],
    evidenceExamples: ["Pathway diagram", "Inspection records of vaults/manholes", "Diversity attestation from carrier"],
    assessmentRubric: {
      implemented: "Armored/conduit pathways for sensitive runs; locked manholes/vaults; diverse entry for redundant circuits.",
      partial: "Some hardening but documented diversity missing.",
      notImplemented: "Open or unprotected cable paths.",
    },
  }),
  ddos_edge_protection: M({
    controlKey: "ddos_edge_protection",
    label: "DDoS / network edge protection",
    module: "B4",
    description: "Network edge defenses against volumetric and application-layer denial-of-service attacks.",
    whyItMatters: "DDoS is a primary availability threat for telecom operators and can cascade across customer services.",
    nistMappings: ["PR.IR-03", "DE.CM-01"],
    telecomStandards: ["RFC 4732", "MANRS"],
    evidenceExamples: ["DDoS mitigation contract / configuration", "Recent mitigation event report", "Tabletop exercise notes"],
    assessmentRubric: {
      implemented: "Always-on scrubbing or upstream mitigation with documented thresholds and periodic drills.",
      partial: "On-demand mitigation only or no drills.",
      notImplemented: "No DDoS mitigation strategy.",
    },
  }),
  site_incident_response: M({
    controlKey: "site_incident_response",
    label: "Site incident response procedures",
    module: "B4",
    description: "Documented procedures for responding to physical, environmental or facility-related incidents.",
    whyItMatters: "A coordinated site response limits impact, accelerates recovery and supports regulatory reporting obligations.",
    nistMappings: ["RS.MA-01", "RC.RP-01"],
    telecomStandards: ["ISO/IEC 27035", "NIST SP 800-61"],
    evidenceExamples: ["Incident response playbook", "On-call roster", "Recent post-incident review"],
    assessmentRubric: {
      implemented: "Site-specific runbooks (fire, power, intrusion); defined escalation chain; post-incident reviews.",
      partial: "Generic IR plan without site-specific runbooks.",
      notImplemented: "No site-level IR procedures.",
    },
  }),

  // ─────────────────────────────────────────────────────────────────────────
  // C1–C8 · Telecom batch 1 (legacy v2_hybrid seed — informational only)
  // ─────────────────────────────────────────────────────────────────────────
  ran_security_hardening: M({
    controlKey: "ran_security_hardening",
    label: "RAN node hardening (eNodeB / gNodeB)",
    module: "C1",
    description: "Baseline hardening of radio access nodes covering management ports, default credentials and patch posture.",
    whyItMatters: "Compromised RAN nodes enable interception, denial of service and lateral movement into the core.",
    nistMappings: ["PR.PS-01", "PR.PS-02"],
    telecomStandards: ["3GPP TS 33.117", "3GPP TS 33.216", "GSMA FS.31"],
    evidenceExamples: ["RAN hardening baseline document", "Patch status report", "Configuration audit sample"],
    assessmentRubric: {
      implemented: "Documented hardening baseline applied to all RAN nodes; quarterly compliance audit; patching SLA met.",
      partial: "Hardening applied inconsistently or audits ad-hoc.",
      notImplemented: "No RAN hardening standard.",
    },
    applicableServiceModels: ["MNO", "Integrated Operator", "Private 5G", "MVNO"],
  }),
  ran_air_interface_protect: M({
    controlKey: "ran_air_interface_protect",
    label: "Air-interface integrity & encryption",
    module: "C1",
    description: "Enforcement of air-interface confidentiality and integrity algorithms across all radio bearers.",
    whyItMatters: "Weak or disabled ciphers expose subscriber traffic to interception and downgrade attacks (e.g., IMSI catchers).",
    nistMappings: ["PR.DS-02", "PR.DS-10"],
    telecomStandards: ["3GPP TS 33.401 (LTE)", "3GPP TS 33.501 (5G)"],
    evidenceExamples: ["Cipher policy configuration", "Null-cipher exception register", "Periodic algorithm audit"],
    assessmentRubric: {
      implemented: "Strong ciphers enforced; null-cipher disabled in production; documented exceptions reviewed quarterly.",
      partial: "Strong ciphers default but null-cipher allowed without governance.",
      notImplemented: "Null or weak ciphers in active use.",
    },
    applicableServiceModels: ["MNO", "Integrated Operator", "Private 5G"],
  }),
  core_5g_sba_security: M({
    controlKey: "core_5g_sba_security",
    label: "5G SBA / service-mesh security",
    module: "C2",
    description: "Service Based Architecture security covering NF discovery, SBI authentication and inter-NF authorization.",
    whyItMatters: "Without SBA security, any compromised NF can impersonate or query others, undermining the entire core trust model.",
    nistMappings: ["PR.AA-01", "PR.DS-02"],
    telecomStandards: ["3GPP TS 33.501", "3GPP TS 29.510 (NRF)"],
    evidenceExamples: ["NRF authentication configuration", "SBI mTLS certificate inventory", "Service-mesh policy export"],
    assessmentRubric: {
      implemented: "mTLS + OAuth2 enforced for all SBI; NRF discovery authenticated; certificate lifecycle automated.",
      partial: "mTLS partial or mixed cleartext SBI in transitional segments.",
      notImplemented: "Cleartext SBI or no NF authentication.",
    },
    applicableServiceModels: ["MNO", "Integrated Operator", "Private 5G"],
  }),
  core_epc_hardening: M({
    controlKey: "core_epc_hardening",
    label: "EPC / IMS element hardening",
    module: "C2",
    description: "Hardening of legacy 4G EPC and IMS elements (MME, SGW, PGW, IMS CSCFs).",
    whyItMatters: "EPC and IMS nodes remain central to voice and data services; weak configurations are common attack vectors.",
    nistMappings: ["PR.PS-01", "PR.PS-02"],
    telecomStandards: ["3GPP TS 33.250", "GSMA FS.31"],
    evidenceExamples: ["EPC/IMS hardening baseline", "Vulnerability scan results", "Configuration drift report"],
    assessmentRubric: {
      implemented: "Documented baseline, quarterly scan, patch SLA enforced.",
      partial: "Baseline exists but inconsistent compliance.",
      notImplemented: "No formal hardening or no patch cadence.",
    },
    applicableServiceModels: ["MNO", "MVNO", "Integrated Operator", "Fixed"],
  }),
  signaling_ss7_security: M({
    controlKey: "signaling_ss7_security",
    label: "SS7 firewall & monitoring",
    module: "C3",
    description: "Filtering and monitoring of SS7 / MAP / CAP signaling at network borders.",
    whyItMatters: "SS7 attacks enable location tracking, SMS interception and call redirection across roaming partners.",
    nistMappings: ["PR.IR-01", "DE.CM-01"],
    telecomStandards: ["GSMA FS.11 (SS7 Cat 1/2/3)", "GSMA IR.82"],
    evidenceExamples: ["SS7 firewall ruleset", "Monthly anomaly report", "Cat-1/2/3 blocking statistics"],
    assessmentRubric: {
      implemented: "GSMA Cat-1/2/3 filtering enforced; continuous monitoring with alerting on anomalies.",
      partial: "Cat-1 only or monitoring without active blocking.",
      notImplemented: "No SS7 firewall or monitoring.",
    },
    applicableServiceModels: ["MNO", "MVNO", "Integrated Operator"],
  }),
  signaling_diameter_security: M({
    controlKey: "signaling_diameter_security",
    label: "Diameter edge agent / firewall",
    module: "C3",
    description: "Diameter Edge Agent (DEA) enforcing topology hiding, rate limiting and message validation.",
    whyItMatters: "Diameter (4G/IMS) inherits SS7-era abuse patterns; an unprotected DEA exposes the HSS and PCRF.",
    nistMappings: ["PR.IR-01", "DE.CM-01"],
    telecomStandards: ["GSMA FS.19 (Diameter Cat 1/2/3)", "3GPP TS 29.273"],
    evidenceExamples: ["DEA configuration", "Topology hiding rules", "Anomaly alerts"],
    assessmentRubric: {
      implemented: "DEA in path; FS.19 filtering enforced; alerts integrated to SOC.",
      partial: "DEA present but limited filtering or no SOC integration.",
      notImplemented: "No DEA or no Diameter filtering.",
    },
    applicableServiceModels: ["MNO", "MVNO", "Integrated Operator"],
  }),
  signaling_gtp_security: M({
    controlKey: "signaling_gtp_security",
    label: "GTP-U / GTP-C firewall",
    module: "C3",
    description: "GTP firewall protecting S5/S8/N4 interfaces from malformed packets and roaming abuse.",
    whyItMatters: "GTP abuse can enable subscriber impersonation, billing fraud and DoS against the packet core.",
    nistMappings: ["PR.IR-01"],
    telecomStandards: ["GSMA FS.20 (GTP)", "3GPP TS 29.281"],
    evidenceExamples: ["GTP firewall ruleset", "Roaming partner whitelist", "Monthly drop statistics"],
    assessmentRubric: {
      implemented: "GTP firewall enforces FS.20 baseline; partner whitelist; alerting active.",
      partial: "Basic ACLs only; no FS.20 alignment.",
      notImplemented: "No GTP filtering.",
    },
    applicableServiceModels: ["MNO", "Integrated Operator"],
  }),
  transport_backhaul_encrypt: M({
    controlKey: "transport_backhaul_encrypt",
    label: "Backhaul / transport encryption (MACsec/IPsec)",
    module: "C4",
    description: "Link-layer or network-layer encryption protecting backhaul and core transport.",
    whyItMatters: "Unencrypted backhaul allows passive interception of subscriber traffic and signaling.",
    nistMappings: ["PR.DS-02"],
    telecomStandards: ["3GPP TS 33.210", "IEEE 802.1AE (MACsec)"],
    evidenceExamples: ["IPsec/MACsec policy", "Tunnel inventory", "Key rotation procedure"],
    assessmentRubric: {
      implemented: "All untrusted backhaul encrypted; documented key rotation; monitored tunnel health.",
      partial: "Encryption on some segments only.",
      notImplemented: "No transport encryption.",
    },
    applicableServiceModels: ["MNO", "MVNO", "Fixed", "ISP", "Wholesale Carrier"],
  }),
  transport_segment_routing: M({
    controlKey: "transport_segment_routing",
    label: "Segment-routing / VRF isolation",
    module: "C4",
    description: "Logical segmentation of transport using SR/SRv6, MPLS L3VPN or VRFs.",
    whyItMatters: "Flat transport allows lateral movement across customer/network slices and compromises tenant isolation.",
    nistMappings: ["PR.IR-01"],
    telecomStandards: ["RFC 4364", "RFC 8402"],
    evidenceExamples: ["Transport segmentation design doc", "VRF/SR policy export", "Inter-VRF leak audit"],
    assessmentRubric: {
      implemented: "Per-tenant/function VRFs with audited leak policies; documented topology.",
      partial: "Segmentation exists but ad-hoc leaks present.",
      notImplemented: "Flat transport.",
    },
    applicableServiceModels: ["MNO", "Fixed", "ISP", "Wholesale Carrier"],
  }),
  nfv_mec_isolation: M({
    controlKey: "nfv_mec_isolation",
    label: "NFV / MEC workload isolation",
    module: "C5",
    description: "Hypervisor / container isolation between virtual network functions and MEC workloads.",
    whyItMatters: "Weak isolation lets a compromised VNF or MEC app pivot into adjacent services or signaling planes.",
    nistMappings: ["PR.IR-01", "PR.PS-01"],
    telecomStandards: ["ETSI NFV-SEC 012", "ETSI MEC 003"],
    evidenceExamples: ["NFVI security architecture", "Hardening baseline (CIS / STIG)", "Tenant isolation tests"],
    assessmentRubric: {
      implemented: "CIS/STIG hardened NFVI; documented tenant boundaries; periodic isolation testing.",
      partial: "Hardening incomplete or no isolation tests.",
      notImplemented: "Default NFVI / no isolation strategy.",
    },
    applicableServiceModels: ["MNO", "Integrated Operator", "Cloud-native Telco", "Private 5G"],
  }),
  oss_bss_access_control: M({
    controlKey: "oss_bss_access_control",
    label: "OSS / BSS privileged access controls",
    module: "C6",
    description: "Privileged access management for OSS, BSS and billing systems.",
    whyItMatters: "OSS/BSS holds subscriber data, billing and provisioning; compromise enables fraud and large-scale data exposure.",
    nistMappings: ["PR.AA-05", "PR.AA-01"],
    telecomStandards: ["TMF 632 (Party Mgmt)", "ISO/IEC 27001 A.5.15–A.5.18"],
    evidenceExamples: ["PAM configuration", "Quarterly access review", "Joiner-mover-leaver workflow"],
    assessmentRubric: {
      implemented: "PAM in use for all privileged OSS/BSS; quarterly reviews; automated JML.",
      partial: "PAM partial or manual JML.",
      notImplemented: "Shared admin accounts or no review.",
    },
    applicableServiceModels: ["MNO", "MVNO", "Fixed", "ISP"],
  }),
  roaming_ipx_security: M({
    controlKey: "roaming_ipx_security",
    label: "Roaming / IPX interconnect security",
    module: "C7",
    description: "Security controls at roaming and IPX interconnect borders.",
    whyItMatters: "Roaming partners and IPX peers are a common entry point for signaling and billing abuse.",
    nistMappings: ["PR.IR-01", "GV.SC-07"],
    telecomStandards: ["GSMA IR.77", "GSMA FS.21"],
    evidenceExamples: ["Roaming agreement security annex", "IPX peer ACLs", "Signaling firewall logs at IPX edge"],
    assessmentRubric: {
      implemented: "Signed roaming security annexes; layered filtering; continuous monitoring of IPX edges.",
      partial: "Some controls but no documented partner annex.",
      notImplemented: "Open IPX interconnect or no partner security oversight.",
    },
    applicableServiceModels: ["MNO", "MVNO", "Integrated Operator"],
  }),
  subscriber_data_protection: M({
    controlKey: "subscriber_data_protection",
    label: "Subscriber identity & data protection (HSS/UDM)",
    module: "C7",
    description: "Protection of HSS/UDM subscriber profiles, authentication vectors and identifiers (IMSI/SUPI).",
    whyItMatters: "HSS/UDM compromise yields permanent subscriber credentials and enables mass impersonation or interception.",
    nistMappings: ["PR.DS-01", "PR.DS-02"],
    telecomStandards: ["3GPP TS 33.501 (SUCI/SUPI)", "GSMA FS.31"],
    evidenceExamples: ["HSS/UDM access controls", "SUPI concealment configuration", "Key vault audit"],
    assessmentRubric: {
      implemented: "SUCI enforced; key material in HSM; least-privilege OSS access; monitored exports.",
      partial: "SUCI partially deployed or HSM not used.",
      notImplemented: "Plaintext IMSI in use; no HSM; broad OSS access.",
    },
    applicableServiceModels: ["MNO", "MVNO", "Integrated Operator"],
  }),
  iot_m2m_gateway_security: M({
    controlKey: "iot_m2m_gateway_security",
    label: "IoT / M2M gateway segmentation",
    module: "C7",
    description: "Segmentation and filtering of IoT/M2M traffic from general subscriber and OSS networks.",
    whyItMatters: "IoT devices are frequently compromised; without segmentation they become a launchpad against the core.",
    nistMappings: ["PR.IR-01", "DE.CM-01"],
    telecomStandards: ["GSMA CLP.13 (IoT Security)", "ETSI EN 303 645"],
    evidenceExamples: ["IoT segmentation diagram", "M2M APN security policy", "Anomaly detection rules"],
    assessmentRubric: {
      implemented: "Dedicated APNs/slices for IoT; per-device rate limits; anomaly detection.",
      partial: "Logical segmentation but no anomaly detection.",
      notImplemented: "IoT traffic mixed with general subscriber traffic.",
    },
    applicableServiceModels: ["MNO", "IoT", "Integrated Operator"],
  }),
  service_continuity_geo_red: M({
    controlKey: "service_continuity_geo_red",
    label: "Geo-redundant service continuity",
    module: "C8",
    description: "Geo-redundant deployment of critical network functions and data stores.",
    whyItMatters: "Single-site failure (regional outage, disaster) must not collapse national services or regulated emergency calling.",
    nistMappings: ["RC.RP-01", "PR.IR-04"],
    telecomStandards: ["ITU-T E.800", "ISO 22301"],
    evidenceExamples: ["Geo-redundancy design", "Failover test reports", "RTO/RPO targets per service"],
    assessmentRubric: {
      implemented: "Critical NFs deployed across ≥2 regions; tested failover ≥ annually.",
      partial: "Geo-redundancy for some NFs only.",
      notImplemented: "Single-site critical NFs.",
    },
    applicableServiceModels: ["MNO", "MVNO", "Fixed", "ISP"],
  }),

  // ─────────────────────────────────────────────────────────────────────────
  // C1–C8 · Telecom batch 2 (Phase 2.2 extension)
  // ─────────────────────────────────────────────────────────────────────────
  ran_mgmt_plane_security: M({
    controlKey: "ran_mgmt_plane_security",
    label: "RAN management-plane hardening (NETCONF/SSH/MFA)",
    module: "C1",
    description: "Secure management-plane access to RAN elements (NETCONF over SSH/TLS, MFA, jump-host enforcement).",
    whyItMatters: "Management-plane compromise gives an attacker direct configuration control of radio assets.",
    nistMappings: ["PR.AA-01", "PR.AA-05", "PR.PS-01"],
    telecomStandards: ["3GPP TS 33.117", "O-RAN WG11 Security"],
    evidenceExamples: ["NETCONF/SSH policy", "MFA enrollment for RAN ops", "Jump-host audit log"],
    assessmentRubric: {
      implemented: "All RAN management via authenticated NETCONF/SSH; MFA enforced; jump-host audited.",
      partial: "MFA optional or direct device access permitted.",
      notImplemented: "Cleartext or unauthenticated management access.",
    },
    applicableServiceModels: ["MNO", "Integrated Operator", "Private 5G"],
  }),
  ran_remote_site_access: M({
    controlKey: "ran_remote_site_access",
    label: "Cell-Site Remote Access (Jump Host / MFA / Vendor Controls)",
    module: "C1",
    description: "Controls around remote and vendor access to cell sites and edge RAN nodes.",
    whyItMatters: "Vendor and field-engineer access is a common backdoor into RAN; weak control here negates upstream hardening.",
    nistMappings: ["PR.AA-01", "PR.AA-05"],
    telecomStandards: ["O-RAN WG11", "GSMA FS.31"],
    evidenceExamples: ["Vendor access policy", "Just-in-time access logs", "MFA enforcement evidence"],
    assessmentRubric: {
      implemented: "Just-in-time vendor access via jump-host with MFA and session recording.",
      partial: "Vendor access via jump-host but no JIT or no recording.",
      notImplemented: "Direct/standing vendor access without MFA.",
    },
    applicableServiceModels: ["MNO", "Integrated Operator", "Private 5G"],
  }),
  core_sbi_api_security: M({
    controlKey: "core_sbi_api_security",
    label: "5G SBI mTLS / OAuth2 / JWT validation",
    module: "C2",
    description: "Enforcement of mTLS, OAuth2 client credentials and JWT validation across the 5G Service Based Interface.",
    whyItMatters: "Missing JWT validation or unauthenticated SBI calls allow NF impersonation across the entire 5G core.",
    nistMappings: ["PR.AA-01", "PR.DS-02"],
    telecomStandards: ["3GPP TS 33.501", "3GPP TS 29.510"],
    evidenceExamples: ["SBI mTLS certificate inventory", "OAuth2 client registry", "JWT validation logs"],
    assessmentRubric: {
      implemented: "mTLS + OAuth2 + JWT validation enforced on all SBI; misuse alerts integrated to SOC.",
      partial: "mTLS only or JWT signature not validated.",
      notImplemented: "Cleartext or unauthenticated SBI.",
    },
    applicableServiceModels: ["MNO", "Integrated Operator", "Private 5G"],
  }),
  core_cnf_hardening: M({
    controlKey: "core_cnf_hardening",
    label: "CNF / Kubernetes hardening for core NFs",
    module: "C2",
    description: "Hardening of Cloud-Native Network Functions and their Kubernetes platforms.",
    whyItMatters: "Container escapes or weak K8s posture in core NFs can compromise multiple network functions simultaneously.",
    nistMappings: ["PR.PS-01", "PR.PS-02", "PR.PS-06"],
    telecomStandards: ["CNCF CIS Kubernetes Benchmark", "3GPP TS 33.848"],
    evidenceExamples: ["CIS K8s benchmark report", "Signed image policy", "Runtime security alerts"],
    assessmentRubric: {
      implemented: "CIS-benchmark compliant clusters; admission control; signed images; runtime monitoring.",
      partial: "Hardening partial; no signed images or no runtime monitoring.",
      notImplemented: "Default K8s posture; unsigned images.",
    },
    applicableServiceModels: ["MNO", "Integrated Operator", "Cloud-native Telco", "Private 5G"],
  }),
  transport_ipsec_3gpp: M({
    controlKey: "transport_ipsec_3gpp",
    label: "IPsec on S1/X2/Xn/N2/N3 (3GPP 33.210)",
    module: "C4",
    description: "IPsec protection of 3GPP-defined transport interfaces per TS 33.210.",
    whyItMatters: "Unprotected S1/Xn/N2/N3 enables passive interception and active manipulation of subscriber and signaling traffic.",
    nistMappings: ["PR.DS-02", "PR.DS-10"],
    telecomStandards: ["3GPP TS 33.210", "3GPP TS 33.310"],
    evidenceExamples: ["IPsec policy export", "SEG inventory", "Tunnel monitoring dashboard"],
    assessmentRubric: {
      implemented: "IPsec enforced on all in-scope interfaces via SEGs; monitored tunnels; documented exceptions.",
      partial: "IPsec on some interfaces; exceptions undocumented.",
      notImplemented: "No IPsec on 3GPP transport.",
    },
    applicableServiceModels: ["MNO", "Integrated Operator", "Private 5G", "Wholesale Carrier"],
  }),
  transport_macsec_backhaul: M({
    controlKey: "transport_macsec_backhaul",
    label: "MACsec on metro / backhaul links",
    module: "C4",
    description: "MACsec encryption on metro Ethernet, fiber backhaul and untrusted L2 links.",
    whyItMatters: "L2 backhaul without MACsec is trivially tappable in physical-access scenarios (vaults, manholes).",
    nistMappings: ["PR.DS-02"],
    telecomStandards: ["IEEE 802.1AE", "IEEE 802.1X-2020"],
    evidenceExamples: ["MACsec link inventory", "Key agreement (MKA) configuration", "Link health metrics"],
    assessmentRubric: {
      implemented: "MACsec on all untrusted L2 links; MKA with documented key rotation.",
      partial: "MACsec on subset only.",
      notImplemented: "No MACsec on metro/backhaul.",
    },
    applicableServiceModels: ["MNO", "Integrated Operator", "FiberCo", "Wholesale Carrier"],
  }),
  transport_segmentation: M({
    controlKey: "transport_segmentation",
    label: "MPLS / SR / SRv6 segmentation & VRF integrity",
    module: "C4",
    description: "Logical segmentation integrity for MPLS, Segment Routing and SRv6 transport.",
    whyItMatters: "Misconfigured VRFs or SR policies leak traffic between tenants or violate regulatory separation.",
    nistMappings: ["PR.IR-01"],
    telecomStandards: ["RFC 4364", "RFC 8402", "RFC 8754"],
    evidenceExamples: ["Segmentation design doc", "VRF leak audit", "Change-control records for SR policies"],
    assessmentRubric: {
      implemented: "Documented segmentation; periodic leak audits; change-control on SR policies.",
      partial: "Segmentation present but no leak audit.",
      notImplemented: "Flat transport or undocumented segmentation.",
    },
    applicableServiceModels: ["MNO", "Integrated Operator", "FiberCo", "Wholesale Carrier", "MVNO"],
  }),
  virt_hypervisor_hardening: M({
    controlKey: "virt_hypervisor_hardening",
    label: "Hypervisor / host hardening (CIS)",
    module: "C5",
    description: "CIS-aligned hardening of hypervisors and host OS in the NFVI / cloud infrastructure.",
    whyItMatters: "Host-layer weaknesses undermine all VNF isolation guarantees on top.",
    nistMappings: ["PR.PS-01", "PR.PS-02"],
    telecomStandards: ["CIS Benchmarks", "ETSI NFV-SEC 012"],
    evidenceExamples: ["CIS benchmark scan", "Patch SLA report", "Drift detection alerts"],
    assessmentRubric: {
      implemented: "CIS scan ≥ quarterly; patch SLA met; drift alerts integrated.",
      partial: "Hardening done at install only; no ongoing scan.",
      notImplemented: "Default hypervisor configuration.",
    },
    applicableServiceModels: ["MNO", "Integrated Operator", "Cloud-native Telco", "Private 5G"],
  }),
  virt_k8s_rbac_admission: M({
    controlKey: "virt_k8s_rbac_admission",
    label: "Kubernetes RBAC & admission control",
    module: "C5",
    description: "Least-privilege RBAC plus admission controllers (OPA/Kyverno) on telecom Kubernetes platforms.",
    whyItMatters: "Overly broad RBAC or absent admission control allows tenant CNFs to break isolation.",
    nistMappings: ["PR.AA-05", "PR.PS-01"],
    telecomStandards: ["CNCF CIS K8s Benchmark"],
    evidenceExamples: ["RBAC audit", "Admission policy bundle", "Policy violation alerts"],
    assessmentRubric: {
      implemented: "Least-privilege RBAC, admission policies enforced, violations alerted.",
      partial: "RBAC defined but no admission control.",
      notImplemented: "Cluster-admin or no policy enforcement.",
    },
    applicableServiceModels: ["MNO", "Integrated Operator", "Cloud-native Telco", "Private 5G"],
  }),
  virt_secrets_hsm: M({
    controlKey: "virt_secrets_hsm",
    label: "Secrets management (HSM / Vault)",
    module: "C5",
    description: "Centralized secrets management using HSM-backed Vault or KMS for keys, certificates and credentials.",
    whyItMatters: "Secrets in code, config or env vars are routinely leaked; HSM-backed storage is the regulator expectation.",
    nistMappings: ["PR.DS-01", "PR.DS-02"],
    telecomStandards: ["FIPS 140-3", "ISO/IEC 27001 A.8.24"],
    evidenceExamples: ["Vault/HSM architecture", "Rotation schedule", "Access audit log"],
    assessmentRubric: {
      implemented: "All workloads use Vault/HSM; documented rotation; access audited.",
      partial: "Vault used for some workloads only.",
      notImplemented: "Secrets stored in config/env or source control.",
    },
    applicableServiceModels: ["MNO", "Integrated Operator", "Cloud-native Telco", "Private 5G"],
  }),
  ossbss_api_security: M({
    controlKey: "ossbss_api_security",
    label: "TMF Open API security (OAuth2, rate-limit)",
    module: "C6",
    description: "Authentication, authorization and rate limiting on TMF Open APIs exposed by OSS/BSS.",
    whyItMatters: "OSS/BSS APIs increasingly drive provisioning and billing; weak API security is a top fraud vector.",
    nistMappings: ["PR.AA-01", "PR.DS-02"],
    telecomStandards: ["TMF 630 API Design Guidelines", "TMF 632"],
    evidenceExamples: ["API gateway config", "OAuth2 client inventory", "Rate-limit policy"],
    assessmentRubric: {
      implemented: "OAuth2 enforced; per-client rate limits; monitored anomaly alerts.",
      partial: "OAuth2 but no rate limits or monitoring.",
      notImplemented: "Static API keys or no API gateway.",
    },
    applicableServiceModels: ["MNO", "MVNO", "Integrated Operator", "FiberCo", "Wholesale Carrier"],
  }),
  ossbss_provisioning_integrity: M({
    controlKey: "ossbss_provisioning_integrity",
    label: "Provisioning / activation anti-fraud controls",
    module: "C6",
    description: "Anti-fraud controls on SIM swap, port-in/port-out, eSIM activation and account changes.",
    whyItMatters: "SIM-swap and account-takeover fraud directly enable financial fraud and 2FA bypass.",
    nistMappings: ["PR.AA-01", "DE.CM-09"],
    telecomStandards: ["GSMA FS.28", "Local regulator SIM-swap guidance"],
    evidenceExamples: ["Anti-fraud rule set", "SIM-swap monitoring dashboard", "Cooling-off policy"],
    assessmentRubric: {
      implemented: "Multi-factor proof for high-risk changes; cooling-off period; real-time fraud monitoring.",
      partial: "Some controls but no fraud monitoring.",
      notImplemented: "No anti-fraud controls on provisioning.",
    },
    applicableServiceModels: ["MNO", "MVNO", "Integrated Operator"],
  }),
  ossbss_audit_logging: M({
    controlKey: "ossbss_audit_logging",
    label: "NMS / EMS / OSS audit logging & retention",
    module: "C6",
    description: "Comprehensive audit logging across NMS, EMS, OSS and BSS systems with regulatory-aligned retention.",
    whyItMatters: "Without complete and protected audit trails, incident investigation and regulatory reporting fail.",
    nistMappings: ["DE.CM-01", "DE.CM-09", "PR.PS-04"],
    telecomStandards: ["ISO/IEC 27001 A.8.15", "Local telecom retention rules"],
    evidenceExamples: ["Log retention policy", "SIEM coverage matrix", "Sample query for privileged actions"],
    assessmentRubric: {
      implemented: "Full coverage; tamper-resistant storage; retention aligned to regulator (typically ≥12 months).",
      partial: "Logs collected but no SIEM or short retention.",
      notImplemented: "Logging absent or local-only.",
    },
    applicableServiceModels: ["MNO", "MVNO", "Integrated Operator", "FiberCo", "Wholesale Carrier"],
  }),
  roaming_cat123_filters: M({
    controlKey: "roaming_cat123_filters",
    label: "GSMA Cat-1/2/3 SS7 + Diameter roaming filters",
    module: "C7",
    description: "GSMA-aligned Cat-1/2/3 filtering for SS7 (FS.11) and Diameter (FS.19) at roaming borders.",
    whyItMatters: "Roaming borders are the principal vector for cross-operator signaling abuse and subscriber tracking.",
    nistMappings: ["PR.IR-01", "DE.CM-01"],
    telecomStandards: ["GSMA FS.11", "GSMA FS.19"],
    evidenceExamples: ["Cat-1/2/3 ruleset", "Monthly block statistics", "Annual signaling audit"],
    assessmentRubric: {
      implemented: "Cat-1/2/3 enforced for SS7 and Diameter; reviewed quarterly; SOC alerting active.",
      partial: "Cat-1 only or one protocol only.",
      notImplemented: "No GSMA-aligned roaming filtering.",
    },
    applicableServiceModels: ["MNO", "Integrated Operator"],
  }),
  continuity_rto_rpo_documented: M({
    controlKey: "continuity_rto_rpo_documented",
    label: "Documented RTO / RPO per critical service",
    module: "C8",
    description: "Documented Recovery Time and Recovery Point Objectives for each critical telecom service.",
    whyItMatters: "Without service-level RTO/RPO, recovery plans cannot be validated and regulator continuity obligations are unverifiable.",
    // ── Phase 2.2.6 mapping correction:
    //    ID.BE-05 removed; ID.IM-04 added; RC.RP-01 retained; RC.RP-02 added.
    nistMappings: ["ID.IM-04", "RC.RP-01", "RC.RP-02"],
    telecomStandards: ["ISO 22301", "ITU-T E.800"],
    evidenceExamples: ["BCP document", "Service criticality matrix", "RTO/RPO register"],
    assessmentRubric: {
      implemented: "Per-service RTO/RPO documented, validated against test results, reviewed annually.",
      partial: "Generic targets only; not per service.",
      notImplemented: "No documented RTO/RPO.",
    },
    applicableServiceModels: ["MNO", "MVNO", "Integrated Operator", "FiberCo", "TowerCo", "Wholesale Carrier", "Private 5G"],
  }),
  continuity_dr_exercises: M({
    controlKey: "continuity_dr_exercises",
    label: "DR / failover exercises (≥ annual)",
    module: "C8",
    description: "Disaster-recovery and failover exercises run at least annually for critical telecom services.",
    whyItMatters: "Untested DR plans routinely fail in real incidents; regulators increasingly expect evidence of live exercises.",
    nistMappings: ["RC.RP-01", "ID.IM-02"],
    telecomStandards: ["ISO 22301", "NIST SP 800-84"],
    evidenceExamples: ["DR exercise schedule", "After-action report", "Remediation tracker"],
    assessmentRubric: {
      implemented: "Annual or more frequent live exercises with documented after-action and remediation tracking.",
      partial: "Tabletop only or exercises overdue.",
      notImplemented: "No DR exercises performed.",
    },
    applicableServiceModels: ["MNO", "MVNO", "Integrated Operator", "FiberCo", "TowerCo", "Wholesale Carrier", "Private 5G"],
  }),
  continuity_nf_redundancy: M({
    controlKey: "continuity_nf_redundancy",
    label: "N+1 redundancy for critical network functions",
    module: "C8",
    description: "N+1 (or better) redundancy posture for critical network functions and supporting infrastructure.",
    whyItMatters: "Single-instance NFs make routine maintenance, faults or attacks into outages.",
    nistMappings: ["PR.IR-04", "RC.RP-01"],
    telecomStandards: ["ITU-T E.800", "TIA-942 Tier"],
    evidenceExamples: ["Redundancy matrix per NF", "Single-points-of-failure register", "Capacity plan"],
    assessmentRubric: {
      implemented: "Documented N+1 for all critical NFs; SPOF register actively closed out.",
      partial: "N+1 for some NFs; SPOF register exists but no remediation.",
      notImplemented: "Single-instance critical NFs.",
    },
    applicableServiceModels: ["MNO", "Integrated Operator", "Private 5G", "Wholesale Carrier"],
  }),
};

// Canonical list of all 44 catalog keys (kept in sync with the migration's
// telecom_infrastructure_control_catalog seed). The parity test asserts that
// INFRASTRUCTURE_CONTROL_METADATA has an entry for each.
export const INFRASTRUCTURE_CATALOG_KEYS: readonly string[] = [
  // Generic (12)
  "facility_access_controls", "visitor_management", "badge_biometric_access",
  "cctv_coverage", "rack_cage_security", "fire_suppression", "hvac_environmental",
  "ups_generator", "network_room_protection", "cable_pathway_security",
  "ddos_edge_protection", "site_incident_response",
  // Telecom batch 1 (15)
  "ran_security_hardening", "ran_air_interface_protect",
  "core_5g_sba_security", "core_epc_hardening",
  "signaling_ss7_security", "signaling_diameter_security", "signaling_gtp_security",
  "transport_backhaul_encrypt", "transport_segment_routing",
  "nfv_mec_isolation", "oss_bss_access_control",
  "roaming_ipx_security", "subscriber_data_protection", "iot_m2m_gateway_security",
  "service_continuity_geo_red",
  // Telecom batch 2 (17)
  "ran_mgmt_plane_security", "ran_remote_site_access",
  "core_sbi_api_security", "core_cnf_hardening",
  "transport_ipsec_3gpp", "transport_macsec_backhaul", "transport_segmentation",
  "virt_hypervisor_hardening", "virt_k8s_rbac_admission", "virt_secrets_hsm",
  "ossbss_api_security", "ossbss_provisioning_integrity", "ossbss_audit_logging",
  "roaming_cat123_filters",
  "continuity_rto_rpo_documented", "continuity_dr_exercises", "continuity_nf_redundancy",
] as const;

export function getInfrastructureControlMetadata(
  controlKey: string,
): InfrastructureControlMetadata | undefined {
  return INFRASTRUCTURE_CONTROL_METADATA[controlKey];
}

/**
 * Phase 2.8.1 — Convert an internal database control_key into a human-readable
 * Title Case fallback. ONLY used when no metadata label is registered for the
 * key. Reviewer/report UI must never render the raw snake_case key directly.
 */
export function humanizeInfrastructureControlKey(controlKey: string): string {
  if (!controlKey) return "Infrastructure control";
  return controlKey
    .split(/[_\-\s]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

/**
 * Phase 2.8.1 — Single source of truth for reviewer/report display of an
 * infrastructure control. Returns:
 *   - title:     human title (metadata label, then fallback label argument,
 *                finally a humanized version of the key — never the raw key)
 *   - nistCodes: array of mapped NIST control codes
 *   - nistBadge: comma-joined NIST codes for badge rendering ("" if none)
 *
 * Callers must NOT display the raw control_key in any reviewer or report
 * surface. See parity test `infrastructureControlMetadata.spec.ts`.
 */
export function resolveInfrastructureControlDisplay(
  controlKey: string,
  fallbackLabel?: string | null,
): { title: string; nistCodes: string[]; nistBadge: string } {
  const meta = INFRASTRUCTURE_CONTROL_METADATA[controlKey];
  const metaLabel = meta?.label && meta.label !== controlKey ? meta.label : null;
  const safeFallback =
    fallbackLabel && fallbackLabel.trim() && fallbackLabel.trim() !== controlKey
      ? fallbackLabel.trim()
      : null;
  const title = metaLabel ?? safeFallback ?? humanizeInfrastructureControlKey(controlKey);
  const nistCodes = meta?.nistMappings ?? [];
  return { title, nistCodes, nistBadge: nistCodes.join(", ") };
}
